@if (loading()) {
<div class="d-flex flex-column align-items-center justify-content-center py-5" style="min-height:300px; margin-top: 100px;">
  <!-- Bouncing Dots Animation -->
  <div class="d-flex gap-2 mb-4 bouncer-container">
    <div class="bouncer-dot bg-primary"></div>
    <div class="bouncer-dot bg-primary"></div>
    <div class="bouncer-dot bg-primary"></div>
  </div>

  <!-- Loading Text -->
  <div class="text-center">
    <h4 class="fw-bold text-dark mb-2">Hang tight!</h4>
    <p class="text-secondary mb-0">Fetching your conversion details...</p>
  </div>
</div>

<style>
.bouncer-container {
  align-items: center;
  height: 40px; /* Gives the dots room to bounce up */
}

.bouncer-dot {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  animation: bounce 0.5s infinite alternate cubic-bezier(0.19, 1, 0.22, 1);
}

/* Stagger the animation delays */
.bouncer-dot:nth-child(2) {
  animation-delay: 0.15s;
}

.bouncer-dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes bounce {
  0% {
    transform: translateY(0);
    opacity: 0.6;
  }
  100% {
    transform: translateY(-20px);
    opacity: 1;
  }
}
</style>
} @else {
<!-- Add pt-4 for more top padding -->
<div class="page-container bg-white text-dark medium fs-5">

  <!-- Breadcrumb -->
  <div class="d-flex align-items-center gap-2 px-4 py-2">
<button type="button" class="btn btn-outline-primary btn-lg rounded-pill" (click)="cancel()">
  <i class="bx bx-arrow-back me-2"></i>Conversion
</button>
    <span class="text-muted">
      Trips <i class="bx bx-chevron-right"></i> {{ QueryStepOneId }}
      <i class="bx bx-chevron-right"></i> Quotes <i class="bx bx-chevron-right"></i> {{ QuoteId }}
    </span>
  </div>

  <!-- ═══════ Quote Used selector ═══════ -->
  <div class="card mx-4">
    <div class="card-body">
      @if (isOnHold()) {
      <div class="fs-5 fw-bold mb-1">Quote Used</div>
      <div class="text-muted small mb-3">Here are the quote details used in the process.</div>
      } @else {
      <div class="fs-5 fw-bold mb-1">Quote Used</div>
      <div class="text-muted small mb-3">Please select and verify the quote used in the process.</div>

      <div class="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
        @for (pkg of packageTypes(); track pkg.QuotePackageTypeId; let idx = $index) {
        <div class="col">
          <div
            class="card h-100"
            [class.border-primary]="selectedPackageTypeId() === pkg.QuotePackageTypeId"
            [class.bg-light]="selectedPackageTypeId() === pkg.QuotePackageTypeId"
            (click)="selectPackage(pkg.QuotePackageTypeId)"
            role="button"
          >
            <div class="card-body">
              <div class="form-check d-flex align-items-center gap-2 mb-2">
                <input
                  class="form-check-input mt-0"
                  type="radio"
                  name="quoteUsedOption"
                  [checked]="selectedPackageTypeId() === pkg.QuotePackageTypeId"
                  (change)="selectPackage(pkg.QuotePackageTypeId)"
                />
                <label class="form-check-label fw-bold text-primary text-decoration-underline">
                  Option {{ idx + 1 }}: {{ pkg.PackageTypeName }}
                </label>
              </div>
              <div>
                <span class="text-info me-1">INR</span>
                <strong class="fs-3">{{ formatCurrency(packageQuotePrice(pkg.QuotePackageTypeId)) }}</strong>
              </div>
            </div>
          </div>
        </div>
        }
      </div>
      }
    </div>
  </div>

  @if (selectedPackage(); as pkg) {

  <!-- ═══════ Package Quote Price ═══════ -->
  <div class="card mx-4">
    <div class="card-body">
      <div class="fs-5 fw-bold mb-3">Package Quote Price</div>

      <div class="p-3 w-25" [class.onhold-price-box]="isOnHold()">
        @if (isOnHold()) {
        <span class="onhold-chip d-inline-block mb-2">Used for Holding</span>
        }
        <div>
          <span class="text-info me-1">INR</span>
          <strong class="fs-2">{{ formatCurrency(packageQuotePrice(pkg.QuotePackageTypeId)) }}</strong>
          @if (isGstIncluded(pkg.QuotePackageTypeId)) {
            <span class="small text-muted ms-1">(inc. {{ quoteHeader()?.GstPercent || 0 }}% GST)</span>
          } @else {
            <span class="small text-muted ms-1">(excl. GST)</span>
          }
          <br>
          <span class="small text-muted ms-2">INR {{ formatCurrency(packageCostPrice(pkg.QuotePackageTypeId)) }} /- (cost price)</span>
        </div>

        @for (line of guestCategoryTotals(pkg.QuotePackageTypeId); track line.label) {
        <div class="small mt-1">
          {{ formatCurrency(line.amount) }} / {{ line.label }} x {{ line.count }} {{ line.paxLabel }}
        </div>
        }
      </div>

      @if (quoteHeader()?.CreatedOn) {
      <div class="small text-muted mt-2">
        Created on {{ quoteHeader()?.CreatedOn | date:'dd MMM yyyy, h:mm a' }}
        @if (tripInfo()?.SalesPersonName) { by {{ tripInfo()?.SalesPersonName }} }
      </div>
      }

      <span class="badge bg-warning text-dark fw-normal mt-2 d-inline-block">Latest Quote</span>

      <div class="d-flex align-items-center gap-2 bg-light border rounded p-3 mt-3">
        <i class="bx bx-calendar"></i>
        <div>
          {{ tripInfo()?.StartDate | date:'dd MMM, yyyy' }} for {{ (tripInfo()?.NoOfNights || 0) + 1 }} Days
          <span class="d-block small text-secondary">{{ totalGuestCount() }} Adults</span>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════ Services: Accommodation ═══════ -->
  <div class="card mx-4">
    <div class="card-body">
      <div class="fs-5 fw-bold mb-3">Services</div>

      <div class="d-flex gap-3 mb-4">
        <div
          class="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary border flex-shrink-0"
          style="width:36px;height:36px;"
        >
          <i class="bx bx-bed"></i>
        </div>
        <div class="flex-grow-1">
          <div class="fs-6 fw-bold mb-2">Accommodation</div>

          <table class="table table-bordered align-middle mb-2">
            <thead class="table-light">
              <tr>
                <th>Night</th><th>Hotel</th><th>Meal</th><th>Rooms</th><th class="text-end">Price</th>
              </tr>
            </thead>
            <tbody>
              @for (group of hotelGroupsByPackage(pkg.QuotePackageTypeId); track (group.main.QuoteHotelId || $index) + '-' + group.nightNumber) {
              <tr>
                <td>
                  {{ group.nightNumber }}{{ ordinal(group.nightNumber) }}
                  <span class="d-block small text-secondary">{{ group.stayDate | date:'dd MMM' }}</span>
                </td>
                <td>
                  <span class="fw-semibold d-block">{{ group.main.HotelName }}</span>
                  <span class="d-block small text-secondary">{{ group.main.LocationName || '-' }}, {{ group.main.HotelCategoryName || '-' }}</span>
                </td>
                <td>{{ group.main.MealPlan || '-' }}</td>
                <td>
                  {{ group.main.NoOfRooms || 1 }} {{ group.main.RoomTypeName || 'Room' }}
                  <span class="d-block small text-secondary">{{ group.main.AWEB || 0 }} AwEB + {{ group.main.CNB || 0 }} CNB</span>
                </td>
                <td class="text-end">
                  <i class="bx bx-check-circle text-success"></i>
                  <span class="small text-secondary">INR</span>
                  {{ formatCurrency(group.maxPrice) }}
                </td>
              </tr>
              } @empty {
              <tr><td colspan="5" class="text-muted">No accommodation added for this package.</td></tr>
              }
            </tbody>
          </table>

          @if (specialInclusionsByPackage(pkg.QuotePackageTypeId).length) {
          <div class="fw-bold mt-3 mb-2">Hotel Special Inclusions</div>
          <table class="table table-bordered align-middle mb-2">
            <thead class="table-light">
              <tr>
                <th>Night</th><th>Hotel</th><th>Location / Category</th><th>Special Inclusion</th><th class="text-end">Price (INR)</th>
              </tr>
            </thead>
            <tbody>
              @for (si of specialInclusionsByPackage(pkg.QuotePackageTypeId); track si.QuoteSpecialInclusionId || $index) {
              <tr>
                <td>
                  <strong>{{ si.NightNumber }}{{ ordinal(si.NightNumber) }}</strong>
                  <div class="text-muted small">{{ nightDate(si.NightNumber) | date:'dd MMM' }}</div>
                </td>
                <td><strong>{{ si.HotelName }}</strong></td>
                <td class="text-muted">{{ hotelLocationCategory(si.HotelId) }}</td>
                <td>
                  <strong>{{ si.SpecialInclusionName }}</strong>
                  @if (si.Comments) { <span class="d-block text-muted small">{{ si.Comments }}</span> }
                </td>
                <td class="text-end"><strong>{{ formatCurrency(si.TotalPrice) }}</strong></td>
              </tr>
              }
            </tbody>
          </table>
          }

          <div class="d-flex justify-content-end mt-2">
            <div class="border rounded px-3 py-2 fw-bold text-end">
              Total <span class="text-secondary small fw-normal">INR</span> {{ formatCurrency(packageHotelTotal(pkg.QuotePackageTypeId) + packageSpecialInclusionTotal(pkg.QuotePackageTypeId)) }}
            </div>
          </div>
        </div>
      </div>

      <!-- ═══════ Transportation and Activities ═══════ -->
      @if (transportServices().length || activityGroups().length) {
      <div class="d-flex gap-3">
        <div
          class="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary border flex-shrink-0"
          style="width:36px;height:36px;"
        >
          <i class="bx bx-car"></i>
        </div>
        <div class="flex-grow-1">
          <div class="fs-6 fw-bold mb-2">Transportation and Activities</div>

          @for (day of daySlots(); track day.dayNumber) {
          @if (dayHasServices(day.dayNumber)) {
          <div class="row g-0 mb-3">
            <div class="col-12 col-md-2">
              <div class="bg-light p-3 h-100 rounded-start">
                <strong class="d-block">{{ day.dayNumber }}{{ ordinal(day.dayNumber) }} Day</strong>
                <span class="small text-secondary">{{ day.date | date:'EEE, dd MMM' }}</span>
              </div>
            </div>
            <div class="col-12 col-md-10">
              <table class="table table-bordered align-middle mb-0">
                <tbody>
                  @for (svc of servicesForDay(day.dayNumber); track svc.QuoteServiceId || $index) {
                  @if (svc.ServiceType == 1) {
                  <tr>
                    <td>
                      <strong class="d-block">{{ serviceTitle(svc) }}</strong>
                      <span class="small text-secondary">{{ serviceSubtitle(svc) }}</span>
                    </td>
                    <td>
                      {{ svc.VehicleTypeName || 'Vehicle' }}
                      <span class="d-block small text-secondary">{{ serviceBreakdown(svc) }}</span>
                    </td>
                    <td class="text-end">
                      <i class="bx bx-check-circle text-success"></i>
                      <span class="small text-secondary">INR</span>
                      {{ formatCurrency(svc.TotalPrice || svc.SellingPrice || 0) }}
                    </td>
                  </tr>
                  }
                  }

                  @for (group of activityGroupsForDay(day.dayNumber); track group.LocationId + '-' + group.ActivityServiceId) {
                  <tr>
                    <td>
                      <strong class="d-block">{{ group.LocationName }}</strong>
                      <span class="small text-secondary">{{ group.ActivityServiceName }}</span>
                    </td>
                    <td>
                      @for (entry of group.entries; track entry.QuoteActivityEntryId || $index) {
                      <div>{{ entry.Qty }} {{ entry.PaxTypeLabel || entry.PaxType || 'Adult' }}
                        <span class="d-block small text-secondary">{{ formatCurrency(entry.Rate) }} × {{ entry.Qty }}</span>
                      </div>
                      }
                    </td>
                    <td class="text-end">
                      <i class="bx bx-check-circle text-success"></i>
                      <span class="small text-secondary">INR</span>
                      {{ formatCurrency(group.total) }}
                    </td>
                  </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
          }
          }

          <div class="d-flex justify-content-end mt-2">
            <div class="border rounded px-3 py-2 fw-bold text-end">
              <div>Total <span class="text-secondary small fw-normal">INR</span> {{ formatCurrency(transportActivityTotal()) }}</div>
              <div class="small text-secondary fw-normal mt-1">Transports: INR {{ formatCurrency(transportTotal()) }} • Activity/Tickets: INR {{ formatCurrency(activityTotal()) }}</div>
            </div>
          </div>
        </div>
      </div>
      }
    </div>
  </div>

  <!-- ═══════ Day-wise Schedule ═══════ -->
  @if (hasAnyDaySchedule()) {
  <div class="card mx-4">
    <div class="card-body">
      <div class="fs-5 fw-bold mb-3"><i class="bx bx-chevron-down"></i> Day-wise Schedule</div>
      @for (day of daySlots(); track day.dayNumber) {
      @if (daySchedule(day.dayNumber); as sched) {
      <div class="row g-3 mb-4">
        <div class="col-12 col-md-2">
          <div class="border rounded overflow-hidden text-center h-100">
            <strong class="d-block bg-info text-white p-2">{{ day.dayNumber }}{{ ordinal(day.dayNumber) }} Day</strong>
            <span class="d-block p-1">{{ day.dayLabel }}</span>
            <b class="d-block p-1">{{ day.date | date:'dd MMM' }}</b>
            <span class="d-block p-1">{{ day.date | date:'yyyy' }}</span>
          </div>
        </div>
        <div class="col-12 col-md-10">
          <div class="fw-bold text-decoration-underline mb-2">{{ sched.title }}</div>
          @if (rawDaySchedule(day.dayNumber); as rawHtml) {
          <div [innerHTML]="rawHtml"></div>
          }
        </div>
      </div>
      }
      }
    </div>
  </div>
  }

  <!-- ═══════ Inclusions / Exclusions ═══════ -->
  <div class="card mx-4">
    <div class="card-body">
      <div class="fs-5 fw-bold mb-3"><i class="bx bx-chevron-down"></i> Inclusions/Exclusions</div>
      <div class="row g-4">
        <div class="col-12 col-md-6">
          <span class="d-inline-block fw-bold border-bottom border-success border-3 pb-1 mb-3">Inclusions</span>
          <ul class="list-unstyled">
            @for (item of effectiveInclusions(); track item.QuoteInclusionId || item.InclusionId || $index) {
            <li class="mb-2"><i class="bx bx-check-circle text-success me-2"></i>{{ inclusionText(item) }}</li>
            } @empty {
            <li class="text-muted">No inclusions added for this quotation.</li>
            }
          </ul>
        </div>
        <div class="col-12 col-md-6">
          <span class="d-inline-block fw-bold border-bottom border-danger border-3 pb-1 mb-3">Exclusions</span>
          <ul class="list-unstyled">
            @for (item of effectiveExclusions(); track item.QuoteExclusionId || item.ExclusionId || $index) {
            <li class="mb-2"><i class="bx bx-x-circle text-danger me-2"></i>{{ exclusionText(item) }}</li>
            } @empty {
            <li class="text-muted">No exclusions added for this quotation.</li>
            }
          </ul>
          <div class="border-start border-warning border-3 ps-2 mt-2 fw-bold">Anything not in inclusions is Excluded</div>
        </div>
      </div>
    </div>
  </div>

  <!-- ═══════ Terms ═══════ -->
  <div class="card mx-4">
    <div class="card-header fw-bold"><i class="bx bx-chevron-down"></i> Terms and Conditions</div>
    <div class="card-body">
      <div class="fs-6 fw-bold mb-3">{{ tripInfo()?.DestinationName || 'Terms' }}</div>
      @if (hasTerms()) {
      @for (term of terms(); track term.TermAndConditionId || $index) {
      <div class="mb-2" [innerHTML]="termHtml(term)"></div>
      }
      } @else {
      <div class="text-muted">No terms and conditions added for this destination.</div>
      }
    </div>
  </div>

  <!-- ═══════ Instalments ═══════ -->
  <div class="card mx-4">
    <div class="card-body">
      <div class="fs-5 fw-bold mb-2">Instalments</div>
      <div class="text-muted small mb-3">
        Please provide tentative instalments for this trip. Basic configuration have been created based on
        payment schedules for different service providers (hotels, cabs etc).
      </div>

      <div class="row row-cols-2 row-cols-md-5 g-3 border rounded p-3 bg-light mb-3">
        <div class="col">
          <div class="small text-muted text-uppercase">Trip Source</div>
          <strong>{{ tripInfo()?.AgencyName || 'Direct Query' }}</strong>
        </div>
        <div class="col">
          <div class="small text-muted text-uppercase">Start Date</div>
          <strong>{{ tripInfo()?.StartDate | date:'dd MMM, yyyy' }}</strong>
        </div>
        <div class="col">
          <div class="small text-muted text-uppercase">Duration</div>
          <strong>{{ durationLabel() }}</strong>
        </div>
        <div class="col">
          <div class="small text-muted text-uppercase">Pax</div>
          <strong>{{ totalGuestCount() }} Adults</strong>
        </div>
        <div class="col">
          <div class="small text-muted text-uppercase">Package ({{ pkg.PackageTypeName }})</div>
          <strong><span class="text-info me-1">INR</span>{{ formatCurrency(packageQuotePrice(pkg.QuotePackageTypeId)) }}</strong>
        </div>
      </div>

      @if (instalmentsDirty()) {
      <div class="alert alert-warning d-flex align-items-start gap-2 mb-3">
        <i class="bx bx-error"></i>
        <div>You have made changes to the instalments. Please review them before conversion.</div>
      </div>
      }

<table class="table table-bordered align-middle">
  <thead class="table-light">
    <tr>
      <th style="width:120px;">#</th>
      <th>Amount (INR)</th>
      <th style="width:260px;">Due Date</th>
      <th style="width:80px;"></th>
    </tr>
  </thead>
  <tbody>
    @for (inst of instalments(); track inst.id; let idx = $index) {
    <tr>
      <td>
        {{ idx + 1 }}{{ ordinal(idx + 1) }}
        @if (isFirstInstalment(inst)) { <span class="badge bg-light text-secondary border ms-1">First</span> }
        @if (isLastInstalment(inst)) { <span class="badge bg-light text-secondary border ms-1">Last</span> }
      </td>
      <td>
        <div class="d-flex align-items-center gap-3">
          <input
            type="number"
            class="form-control form-control-lg"
            style="max-width:200px;"
            [value]="inst.amount"
            (change)="updateInstalmentAmount(inst, $any($event.target).value)"
          />
          <i class="bx bx-transfer-alt text-muted fs-3"></i>
          <input
            type="number"
            class="form-control form-control-lg"
            style="max-width:150px;"
            [value]="inst.percent"
            (change)="updateInstalmentPercent(inst, $any($event.target).value)"
          />
          <span class="text-muted fs-6">%</span>
        </div>
      </td>
      <td>
        <input
          type="date"
          class="form-control form-control-lg"
          style="max-width:240px;"
          [value]="inst.dueDate"
          (change)="updateInstalmentDueDate(inst, $any($event.target).value)"
        />
      </td>
      <td>
        @if (instalments().length > 1) {
        <button type="button" class="btn btn-outline-secondary btn-lg" (click)="removeInstalment(inst)">
          <i class="bx bx-x fs-4"></i>
        </button>
        }
      </td>
    </tr>
    @if (remainingAmount() > 0.01) {
    <tr>
      <td colspan="4" class="text-muted small py-3" role="button" (click)="addInstalment()">
        + Add {{ formatCurrency(remainingAmount()) }} /-
      </td>
    </tr>
    }
    }
  </tbody>
</table>

     <div class="d-flex align-items-center gap-3 mt-2">
  <button type="button" class="btn btn-link btn-lg text-decoration-none" (click)="addInstalment()">
    <i class="bx bx-plus me-1 fs-2"></i>Add Another Instalment
  </button>
  @if (remainingAmount() > 0.01) {
  <span class="badge bg-warning text-dark fw-normal fs-6 py-2 px-3">Remaining: {{ formatCurrency(remainingAmount()) }} /-</span>
  }
</div>

      <div class="mt-4">
        <label class="form-label">Comments</label>
        <textarea
          class="form-control"
          rows="3"
          placeholder="Any comments regarding verification or prices etc.."
          [value]="comments()"
          (change)="updateComments($any($event.target).value)"
        ></textarea>
      </div>

      <div class="alert alert-warning mt-4 mb-0">
        <div class="d-flex gap-2">
          <i class="bx bx-error"></i>
          <div>
            <div>Please make sure the above details are correct and have been verified with the client.</div>
            <div>If verified, please confirm by selecting checkbox below</div>
          </div>
        </div>
        <hr />
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="verifyCheck" [checked]="verified()" (change)="toggleVerified()">
          <label class="form-check-label fw-semibold" for="verifyCheck">Yes. I have verified the details</label>
        </div>
      </div>

      <div class="d-flex align-items-center gap-2 mt-4">
        @if (!isOnHold()) {
        <button type="button" class="btn btn-warning" [disabled]="!canSubmit()" (click)="sendForHolding()">
          Send for Holding
        </button>
        }
        <button type="button" class="btn btn-primary" [disabled]="!canSubmit()" (click)="convertTrip()">
          Convert Trip
        </button>
        <button type="button" class="btn btn-link text-decoration-none" (click)="cancel()">Cancel</button>
      </div>
    </div>
  </div>

  } @else {
  <div class="card mx-4">
    <div class="card-body text-muted">This trip has no package options to convert yet.</div>
  </div>
  }

</div>
}

<style>
  .onhold-price-box {
    border: 2px solid #f59e0b;
    border-radius: 8px;
    background: #fffbeb;
  }

  .onhold-chip {
    background: #fef3c7;
    color: #92400e;
    font-weight: 700;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 5px;
  }
</style>